/**
 * A1 · sign in — SPEC §3.2.
 *
 * The cookie is the server's: `HttpOnly; SameSite=Lax; Path=/api`, `Secure` over https, minted by
 * `POST /api/auth` from an email and its password and re-checked against the server's `admins` list
 * on every request. **The browser never reads it and never stores a token.** Nothing in this file
 * touches localStorage, and nothing holds the password past the one POST that spends it.
 *
 * The sliding session is invisible: main.py's `Around` middleware re-mints the cookie past half
 * its life on any request, and the browser does nothing.
 */

import { Failed, api, newRequestId, read, write } from "./client";

/** The states A1 renders: four straight off a 401 body, `verifying` while the POST is out, and
 *  `sign-in failed` when no answer came back at all. */
export type SignInState =
  | "idle"
  | "verifying"
  | "session ended"
  | "rejected"
  | "removed from the list"
  | "sign-in failed";

export interface Who {
  email: string;
  via: "password" | "shell";
}

/** The states a 401 body can name. Anything else is treated as *session ended*, which is the
 *  honest reading: a cookie arrived and did not work. */
const KNOWN: SignInState[] = ["idle", "session ended", "rejected", "removed from the list"];

export function asSignInState(name: string | undefined): SignInState {
  return (KNOWN as string[]).includes(name ?? "") ? (name as SignInState) : "session ended";
}

/** `GET /api/auth` — which of the four it was, on a stream that closed or a request that 401'd. */
export async function currentSession(): Promise<
  { signedIn: true; who: Who } | { signedIn: false; state: SignInState; says: string }
> {
  try {
    const { data } = await read<Who>("/auth");
    return { signedIn: true, who: data };
  } catch (error) {
    if (error instanceof Failed && error.trouble.kind === "signed out") {
      return {
        signedIn: false,
        state: asSignInState(error.trouble.refusal.state),
        says: error.trouble.refusal.says,
      };
    }
    throw error;
  }
}

export async function signIn(email: string, password: string): Promise<Who> {
  return await write<Who>("/auth", { email, password }, newRequestId());
}

export async function signOut(): Promise<void> {
  await fetch(api("/auth"), { method: "DELETE", credentials: "include" });
}

